import React, { forwardRef, ReactNode } from "react";
import Select, {
  OptionProps,
  GroupBase,
  DropdownIndicatorProps,
  Props,
  MultiValueGenericProps,
  MenuListProps,
  SelectInstance,
  components,
  StylesConfig,
} from "react-select";
import ExpandMoreIcon from "../../../shared/components/icons/ExpandMoreIcon";
import Checkbox from "../../../shared/components/ui/Checkbox";
import { Box } from "../../../shared/components/ui/chakra";
import { CloseButton, HStack } from "@chakra-ui/react";

export type OptionType = {
  value: string;
  label: string;
  sublabel?: string;
};

export type MultiSelectProps = Props<
  OptionType,
  true,
  GroupBase<OptionType>
> & { isInvalid?: boolean; inlineOption?: boolean };

const CheckboxOption = ({
  innerProps,
  isSelected,
  label,
  selectProps,
}: OptionProps<OptionType, true>) => {
  const supportedPrefectureStyle = {
    display: "inline-flex",
    marginRight: "1rem",
    marginBottom: "0.25rem",
  };

  return (
    <Box
      {...innerProps}
      cursor="pointer"
      w="fit-content"
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      style={selectProps.inlineOption ? supportedPrefectureStyle : undefined}
    >
      <Box pointerEvents={"none"}>
        <Checkbox
          isChecked={isSelected}
          inputProps={{ onClick: (e) => e.stopPropagation() }}
        >
          {label}
        </Checkbox>
      </Box>
    </Box>
  );
};

const DropdownIndicator = (
  props: DropdownIndicatorProps<OptionType, true>,
) => {
  return (
    <components.DropdownIndicator {...props}>
      <ExpandMoreIcon boxSize="1.25rem" />
    </components.DropdownIndicator>
  );
};

const MultiValueLabel = (props: MultiValueGenericProps<OptionType>) => {
  const sublabel = (props.data as OptionType).sublabel;
  return (
    <HStack gap={0} maxWidth="full" overflow="hidden">
      <components.MultiValueLabel {...props} />
      {sublabel && (
        <Box fontSize="xs" color="textGray">
          {sublabel}
        </Box>
      )}
    </HStack>
  );
};

const MenuList = (props: MenuListProps<OptionType, true>) => {
  return (
    <components.MenuList {...props}>
      <CloseButton
        float="right"
        onClick={props.selectProps.onMenuClose}
        aria-label="閉じる"
      />
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-expect-error */}
      {props.selectProps.menuListContent}
      {props.children}
    </components.MenuList>
  );
};

const customStyles: StylesConfig<OptionType, true> = {
  control: (provided, state) => ({
    ...provided,
    borderColor: "#99A9B0",
    minHeight: "2.5rem",
    borderRadius: "2px",
    pointerEvents: state.isDisabled ? "auto" : "auto",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: state.hasValue ? "0.5rem 0.75rem 0.5rem 0.75rem" : "0 0 0 0.75rem",
  }),
  multiValue: (provided) => ({
    ...provided,
    margin: "0.25rem 0.75rem 0.25rem 0",
    backgroundColor: "#F5F5F5",
    borderRadius: "100px",
    // 38pxはドロップダウンアイコンの幅
    maxWidth: "calc(100% - 38px)",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    padding: "0.25rem 0.75rem",
    paddingLeft: "none",
    fontSize: "0.875rem",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    position: "absolute",
    right: "0.25rem",
    top: "0.125rem",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
  }),
  clearIndicator: (provided) => ({
    ...provided,
    display: "none",
  }),
  placeholder: (provided, state) => {
    return {
      ...provided,
      color: state.isDisabled ? "#6B767B" : "#212529",
    };
  },
  multiValueRemove: (provided, { selectProps }) => ({
    ...provided,
    paddingLeft: "0.5rem",
    paddingRight: "0.5rem",
    display: selectProps.isDisabled ? "none" : "flex",
  }),
  menu: (provided) => ({
    ...provided,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "1rem 1.25rem",
    maxHeight: "320px",
  }),
  groupHeading: (provided) => ({
    ...provided,
    fontSize: "0.75rem",
    color: "#212529",
    borderBottom: "1px solid #C4CCCA",
    paddingBottom: "0.25rem",
    paddingLeft: 0,
    marginBottom: "0.5rem",
  }),
  group: (provided) => ({
    ...provided,
    marginBottom: "1rem",
    paddingBottom: 0,
  }),
  option: (provided) => ({
    ...provided,
    padding: "0.25rem 0",
    marginBottom: "0.25rem",
  }),
};

const Option = (optionProps: OptionProps<OptionType, true>) => (
  <CheckboxOption {...optionProps} />
);

const MultiSelect = forwardRef<
  SelectInstance<OptionType, true>,
  { menuListContent?: ReactNode } & MultiSelectProps
>(function MultiSelect(
  { menuListContent, isInvalid, inlineOption = false, ...props },
  ref,
) {
  return (
    <Select<OptionType, true, GroupBase<OptionType>>
      {...props}
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      inlineOption={inlineOption}
      menuListContent={menuListContent}
      components={{
        Option,
        DropdownIndicator,
        MultiValueLabel,
        MenuList,
      }}
      styles={customStyles}
      placeholder="選択してください"
      aria-invalid={isInvalid}
      ref={ref}
    />
  );
});

export default MultiSelect;
